import { Fragment, type ReactNode } from 'react';

/**
 * Renders the `*emphasis*` convention used by the argument sections' copy.
 *
 * The copy lives in `app/_shared/marketing/landingContent.ts`, which is a plain-string module
 * shared with the React Native app — so the emphasis cannot be HTML in the string, and it must
 * not need `dangerouslySetInnerHTML` at the call site. A marker plus one renderer keeps the
 * strings portable: web turns `*…*` into <strong>, and an RN consumer would map the same split
 * onto a nested <Text>.
 *
 * Deliberately not a markdown parser. One marker, no nesting, no escapes — anything richer than
 * that belongs in the layout, not inside a sentence.
 *
 * NOTE for future consumers: a string containing markers must go through this (or an equivalent),
 * or the asterisks render literally. Only marketing-only exports carry them today.
 */
export function withEmphasis(text: string): ReactNode {
  if (!text.includes('*')) return text;
  // Split on the marked runs, keeping them: odd indices are the emphasised pieces.
  const parts = text.split(/\*([^*]+)\*/g);
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <strong key={i} className="ml-em">
        {part}
      </strong>
    ) : (
      <Fragment key={i}>{part}</Fragment>
    )
  );
}
